import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import customListState from "Pages/Dashboard/dataSearch/CustomList/state";
import Button from "Components/common/Button";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import makeApi, { URLS } from "Utils/apiURL";
import { MP_EVENT } from "Static/constant";
import Utils from "Utils/utils";
import SequenceUtils from "Utils/SequenceUtils";
import bulkJobsNotificationStore from "Stores/bulkJobsNotification";
import importFromContactsListPopupState from "./importFromContactsListPopupState";
import runImportFromContactsListGuards from "Utils/sequenceImportFromContactsListGuards";

const listRowKey = list => {
  if (list == null) {
    return "";
  }
  if (list.id != null && list.id !== "") {
    return String(list.id);
  }
  return `name:${list.name || ""}`;
};

const buildAddListToSequencePayload = (list, sequenceId) => {
  const totalCount =
    typeof list?.count === "number" ? list.count : parseInt(list?.count, 10) || 0;
  return {
    source: "CONTACT",
    type: "ADD_TO_SEQUENCE",
    totalCount,
    all: true,
    search: {
      stageId: null,
      searchTerm: "",
      filter: {},
      listId: list?.id
    },
    filter: {},
    data: {
      sequenceId
    }
  };
};

const ImportFromContactsListPopup = observer(() => {
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [importLoading, setImportLoading] = useState(false);

  const visible = importFromContactsListPopupState.visible;
  const bootstrapPhase = importFromContactsListPopupState.bootstrapPhase;
  const ready = bootstrapPhase === "ready";
  const sequence = importFromContactsListPopupState.sequence || {};
  const sequenceId = importFromContactsListPopupState.sequenceId || "";

  const rawContactLists = ready ? customListState.totalContactList || [] : [];
  const searchTrim = search.trim().toLowerCase();
  const lists = searchTrim
    ? rawContactLists.filter(item =>
        (item?.name || "").toLowerCase().includes(searchTrim)
      )
    : rawContactLists;

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    let cancelled = false;
    setSearch("");
    setSelectedKey("");
    setImportLoading(false);

    const run = async () => {
      try {
        const after = importFromContactsListPopupState.afterGuardsFn;
        if (typeof after === "function") {
          await after();
        }
        if (cancelled) {
          return;
        }
        const refresh = importFromContactsListPopupState.refreshSequenceFn;
        if (typeof refresh === "function") {
          const nextSeq = refresh();
          if (nextSeq) {
            importFromContactsListPopupState.setSequence(nextSeq);
          }
        }
        const ctx = importFromContactsListPopupState.guardContext;
        const seq = importFromContactsListPopupState.sequence;
        const sid = importFromContactsListPopupState.sequenceId;
        if (!ctx || !sid) {
          if (!cancelled) {
            importFromContactsListPopupState.close();
          }
          return;
        }
        const ok = await runImportFromContactsListGuards({
          ...ctx,
          sequence: seq,
          sequenceId: sid
        });
        if (cancelled) {
          return;
        }
        if (!ok) {
          importFromContactsListPopupState.close();
          return;
        }
        await customListState.getContactList();
        if (cancelled) {
          return;
        }
        importFromContactsListPopupState.setBootstrapPhase("ready");
      } catch (e) {
        if (!cancelled) {
          importFromContactsListPopupState.close();
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !ready || !lists.length) {
      return;
    }
    if (!lists.some(l => listRowKey(l) === selectedKey)) {
      setSelectedKey(listRowKey(lists[0]));
    }
  }, [visible, ready, lists, selectedKey]);

  const selectedList = useMemo(
    () => (ready ? lists.find(l => listRowKey(l) === selectedKey) || null : null),
    [ready, lists, selectedKey]
  );

  const closePopup = () => {
    importFromContactsListPopupState.close();
  };

  const requestImport = () => {
    if (!selectedList || !sequenceId || importLoading || !ready) {
      return;
    }
    const totalCount =
      typeof selectedList.count === "number"
        ? selectedList.count
        : parseInt(selectedList.count, 10) || 0;
    const listName = selectedList.name || "this list";
    const seqName = sequence.name || "this sequence";
    const contactLabel = totalCount === 1 ? "contact" : "contacts";

    confirmationPopupState.setPopupValues({
      content: `Add ${totalCount} ${contactLabel} from "${listName}" to the sequence "${seqName}"?`,
      actionBtnText: "Confirm",
      cancelBtnText: "Cancel",
      needCancelBtn: true,
      cancelCbk: () => confirmationPopupState.setShowPopup(false),
      callback: () => {
        confirmationPopupState.setShowPopup(false);
        runImportRequest();
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const runImportRequest = async () => {
    if (!selectedList || !sequenceId) {
      return;
    }
    const sequenceForTracking = importFromContactsListPopupState.sequence;
    setImportLoading(true);
    Utils.reloadFeatureFlag(false);
    const data = buildAddListToSequencePayload(selectedList, sequenceId);
    let shouldClose = false;
    try {
      const response = await makeApi({
        url: URLS.performContactsBulkOperation,
        method: "POST",
        data
      });
      if (response?.data) {
        if (SequenceUtils.tryHandleAddToSequenceBulkConflict(response.data)) {
          shouldClose = false;
        } else {
          const { success = false, background = false } = response.data;
          if (success) {
            shouldClose = true;
            if (background) {
              bulkJobsNotificationStore.fetchJobs();
              bulkJobsNotificationStore.triggerGuide(
                "Your add-to-sequence job has started. Track it here."
              );
            } else {
              const addedAny = SequenceUtils.handleAddToSequenceBulkResult(
                response.data
              );
              if (addedAny) {
                const onImportSuccess =
                  importFromContactsListPopupState.onImportSuccessFn;
                if (typeof onImportSuccess === "function") {
                  Promise.resolve(onImportSuccess()).catch(() => {});
                }
                Utils.mixpanelTrack(MP_EVENT.SQ_STARTED, {
                  sequenceName: sequenceForTracking?.name,
                  pageType: "Contact",
                  source: "import_from_contacts_list"
                });
              }
            }
          } else {
            toasterState.setToastMsg(
              "Something went wrong. Please try again later",
              "fail"
            );
          }
        }
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "fail"
        );
      }
    } catch (e) {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
    } finally {
      setImportLoading(false);
    }
    if (shouldClose) {
      closePopup();
    }
  };

  if (!visible) {
    return null;
  }

  const count =
    selectedList &&
    (typeof selectedList.count === "number"
      ? selectedList.count
      : parseInt(selectedList.count, 10) || 0);

  return (
    <div className="popupWrapper importFromContactsListPage">
      <div className="confirmation-popup common-popup">
        <div className="popup-wrap animated customzoomIn importFromContactsListWrap">
          <div className="title">
            Import from Contacts
            <div
              className="popup-close"
              title="Click here to close"
              onClick={closePopup}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && closePopup()}
            >
              <i className="material-icons">close</i>
            </div>
          </div>
          <div className="popup-content-wrap importFromContactsListBody">
            <p className="importFromContactsListHint">
              Choose a contact list to add everyone in that list to this sequence.
            </p>
            <input
              type="text"
              className="importFromContactsListSearch"
              placeholder="Search lists…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={!ready}
            />
            <div className="importFromContactsListScroll">
              {!ready ? (
                <div className="importFromContactsListLoader" aria-busy="true">
                  <i className="material-icons importFromContactsListSpinner">
                    sync
                  </i>
                  <span>Loading lists…</span>
                </div>
              ) : lists.length === 0 ? (
                <div className="importFromContactsListEmpty">
                  No lists match your search.
                </div>
              ) : (
                lists.map(list => {
                  const key = listRowKey(list);
                  const c =
                    typeof list.count === "number"
                      ? list.count
                      : parseInt(list.count, 10) || 0;
                  return (
                    <label
                      key={key}
                      className={`importFromContactsListRow ${
                        selectedKey === key ? "isSelected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="importFromContactsListChoice"
                        checked={selectedKey === key}
                        onChange={() => setSelectedKey(key)}
                      />
                      <span className="importFromContactsListRowMain">
                        <span
                          className="importFromContactsListRowName"
                          title={list.name}
                        >
                          {list.name}
                        </span>
                        <span className="importFromContactsListRowCount">
                          {Utils.formatNumberToStringInUSFormat(c)} contact
                          {c === 1 ? "" : "s"}
                        </span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <div className="popup-action-wrap importFromContactsListActions">
            <Button
              id="importFromContactsListCancel"
              name="importFromContactsListCancel"
              type="button"
              btnText="Cancel"
              className="btn importFromContactsListCancelBtn"
              loader={false}
              click={closePopup}
            />
            <Button
              id="importFromContactsListSubmit"
              name="importFromContactsListSubmit"
              type="button"
              btnText="Import"
              className={`btn nextBtn importFromContactsListImportBtn ${
                importLoading ? "loading" : ""
              }`}
              loader={importLoading}
              disabled={importLoading || !ready || !selectedList || !count}
              click={requestImport}
            />
          </div>
        </div>
      </div>
      <div className="common-popup-mask" />
    </div>
  );
});

export { ImportFromContactsListPopup };
export default ImportFromContactsListPopup;
